import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

/**
 * Tracks sample-data lifecycle for the current user.
 * - hasSample: any is_mock=true accounts exist
 * - loadedOnce: one-time Settings activation already used
 * - cleanupChoice: stored decision when user first connected a real/demo account
 */
export const useSampleDataStatus = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["sample-data-status", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return null;

      const [{ count: sampleAccountCount }, { data: progress }] = await Promise.all([
        supabase
          .from("accounts")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("is_mock", true as never),
        supabase
          .from("user_tour_progress")
          .select("mock_data_loaded_once, mock_cleanup_choice")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);

      const p = (progress ?? {}) as {
        mock_data_loaded_once?: boolean;
        mock_cleanup_choice?: string | null;
      };

      return {
        hasSample: (sampleAccountCount ?? 0) > 0,
        loadedOnce: p.mock_data_loaded_once === true,
        cleanupChoice: p.mock_cleanup_choice ?? null,
      };
    },
  });
};

/**
 * One-time activation of sample data for existing users.
 * Backend RPC enforces idempotency (returns false if already loaded).
 */
export const useLoadSampleData = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      const { data, error } = await supabase.rpc(
        "seed_sample_data_for_user" as never,
        { _user_id: user.id } as never,
      );
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: (loaded) => {
      queryClient.invalidateQueries({ queryKey: ["sample-data-status"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["trades"] });
      queryClient.invalidateQueries({ queryKey: ["tour-progress"] });
      toast({
        title: loaded ? "Sample data loaded" : "Already loaded",
        description: loaded
          ? "Your workspace is now populated with sample trades and plans."
          : "Sample data was already activated previously.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to load sample data",
        description: err.message,
        variant: "destructive",
      });
    },
  });
};

/** Permanently delete all sample data for the user. */
export const useCleanupSampleData = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase.rpc(
        "cleanup_sample_data" as never,
        { _user_id: user.id } as never,
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sample-data-status"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["trades"] });
      toast({
        title: "Sample data removed",
        description: "All sample trades, plans and accounts have been deleted.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to remove sample data",
        description: err.message,
        variant: "destructive",
      });
    },
  });
};

/** Persist user's cleanup choice (delete | keep | never_ask). */
export const useSetCleanupChoice = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (choice: "delete" | "keep" | "never_ask") => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("user_tour_progress")
        .update({ mock_cleanup_choice: choice } as never)
        .eq("user_id", user.id);
      if (error) throw error;
      return choice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sample-data-status"] });
      queryClient.invalidateQueries({ queryKey: ["tour-progress"] });
    },
  });
};
